import React, { useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight, EyeOff, Lightbulb, X } from 'lucide-react';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import type { VorankVorschlag } from '../../services/vorankuendigungSteckbrief';

/** Ergebnis des Stempelns `uebernommen_am` nach einer Übernahme — eine Zeile, schließbar. */
export interface StempelMeldung {
    art: 'ok' | 'fehler';
    text: string;
}

interface Props {
    /** Alle Zeilen aus `vorankuendigungVorschlaege` — die Leiste blendet selbst aus. */
    vorschlaege: readonly VorankVorschlag[];
    /** In dieser Sitzung ausgeblendete Ids; gespeichert wird nichts. */
    ausgeblendet: ReadonlySet<string>;
    isReadOnly: boolean;
    /** Lesefehler des Steckbriefs als Satz — damit eine leere Leiste nie wie „alles geklärt" aussieht. */
    lesefehler: string | null;
    stempel: StempelMeldung | null;
    onUebernehmen: (vorschlag: VorankVorschlag) => void;
    onAusblenden: (id: string) => void;
    onStempelSchliessen: () => void;
}

/**
 * Leiste „Aus der Planungsphase" über dem Formular der Vorankündigung
 * (Plan Planungsphase, Stufe 3.1).
 *
 * Sie zeigt nur an und ruft auf. Welche Zeilen es gibt und was eine
 * Übernahme am Datensatz ändert, steht geprüft in
 * `services/vorankuendigungSteckbrief.ts`.
 *
 * Zusagen an den Anwender:
 *  - Nichts wird von selbst gesetzt. Jede Zeile nennt den Planungs- bzw.
 *    Projektwert, den aktuellen Wert und die Quelle; geschrieben wird erst mit
 *    „Übernehmen" — über denselben Weg wie eine Eingabe (Autospeicher).
 *  - Eine Zeile verschwindet, sobald das Feld den Wert trägt, gleich ob durch
 *    Übernahme oder eigene Eingabe.
 *  - „Ausblenden" gilt nur für diese Sitzung.
 *  - Im Lesemodus bleibt die Leiste lesbar, die Knöpfe sind gesperrt.
 */
export const VorankuendigungPlanungsLeiste: React.FC<Props> = ({
    vorschlaege, ausgeblendet, isReadOnly, lesefehler, stempel, onUebernehmen, onAusblenden, onStempelSchliessen,
}) => {
    const [offen, setOffen] = useState(true);

    const sichtbar = vorschlaege.filter(v => !ausgeblendet.has(v.id));
    if (sichtbar.length === 0 && !lesefehler && !stempel) return null;

    const knopf = 'inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border '
        + 'disabled:opacity-40 disabled:cursor-not-allowed';

    return (
        <section className="bg-blue-50 border border-blue-200 rounded-2xl mb-4 print:hidden">
            <div className="flex items-center gap-2 px-4 py-2.5">
                <button
                    type="button"
                    onClick={() => setOffen(o => !o)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    aria-expanded={offen}
                >
                    {offen ? <ChevronDown className="w-4 h-4 text-blue-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-blue-500 shrink-0" />}
                    <Lightbulb className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="text-sm font-bold text-blue-900 truncate">
                        Aus der Planungsphase ({sichtbar.length})
                    </span>
                </button>
            </div>

            {offen && (
                <div className="border-t border-blue-200">
                    <p className="px-4 pt-2.5 text-[11px] text-blue-800 leading-snug">
                        Aus Planungsphase und Projektdaten abgeleitet — nichts davon ist gesetzt. Übernommen wird je
                        Zeile per Klick; die Eingaben bleiben sonst stehen. „Ausblenden“ gilt nur für diese Sitzung.
                    </p>
                    {lesefehler && (
                        <div className="mx-4 mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span className="min-w-0 break-words">Nicht alles konnte gelesen werden — die Leiste ist unvollständig. {lesefehler}</span>
                        </div>
                    )}
                    {stempel && (
                        <div className={`mx-4 mt-2 text-xs border rounded-lg px-3 py-2 flex items-start gap-2 ${
                            stempel.art === 'ok'
                                ? 'text-emerald-800 bg-emerald-50 border-emerald-200'
                                : 'text-amber-800 bg-amber-50 border-amber-200'
                        }`}>
                            {stempel.art === 'ok'
                                ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                                : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
                            <span className="flex-1 min-w-0 break-words">{stempel.text}</span>
                            <button
                                type="button"
                                onClick={onStempelSchliessen}
                                title="Hinweis ausblenden"
                                className="shrink-0 p-0.5 rounded hover:bg-black/5 transition"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                    <ul className="divide-y divide-blue-100">
                        {sichtbar.map(v => (
                            <li key={v.id} className="px-4 py-2.5 flex flex-col md:flex-row md:items-start gap-2 md:gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs text-slate-800 leading-snug">
                                        <span className="font-bold">{v.punkt}</span>
                                        {v.art === 'uebernahme'
                                            ? <>
                                                <span className="text-slate-400"> → </span>
                                                <span className="font-bold text-blue-700 whitespace-pre-line break-words">{v.planung}</span>
                                            </>
                                            : <span className="text-slate-700 break-words">: {v.planung}</span>}
                                    </div>
                                    {v.aktuell !== undefined && (
                                        <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                            <span className="font-bold">Aktuell in der Vorankündigung:</span>{' '}
                                            <span className="whitespace-pre-line break-words">{v.aktuell}</span>
                                        </div>
                                    )}
                                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                                        <span className="font-bold">Quelle:</span> {v.quelle}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {v.art === 'uebernahme' && (
                                        <button
                                            type="button"
                                            onClick={() => onUebernehmen(v)}
                                            disabled={isReadOnly}
                                            title={isReadOnly
                                                ? readOnlyTitle('Übernehmen ist nicht möglich.')
                                                : 'Den Wert in dieses Feld der Vorankündigung schreiben'}
                                            className={`${knopf} text-blue-700 bg-white border-blue-300 hover:bg-blue-100`}
                                        >
                                            <Check className="w-3.5 h-3.5" /> Übernehmen
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => onAusblenden(v.id)}
                                        disabled={isReadOnly}
                                        title={isReadOnly
                                            ? readOnlyTitle('Ausblenden ist nicht möglich.')
                                            : 'Für diese Sitzung ausblenden — nichts wird gespeichert'}
                                        className={`${knopf} text-slate-500 bg-white border-slate-200 hover:bg-slate-50`}
                                    >
                                        <EyeOff className="w-3.5 h-3.5" /> Ausblenden
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};
